import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import { LogNotification, logServiceDeleteNotify, logServiceListNotifications, logServiceReadNotify } from '@/api';
import { Notification as ToastNotification } from '@/shared/ui/notifications/notifications';
import { subscribeNotificationStream } from './notificationStreamManager';
import { shouldShowNotificationInCenter } from './shouldShowNotificationInCenter';

const showErrorToast = () => {
    toast(<ToastNotification type="warning" text={t('unknownError', { ns: 'global' })} />);
};

export const useNotifications = (
    enabled: boolean,
    onIncomingNotification?: (notification: LogNotification) => void
) => {
    const [notifications, setNotifications] = useState<LogNotification[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isMutating, setIsMutating] = useState(false);
    const onIncomingNotificationRef = useRef(onIncomingNotification);
    onIncomingNotificationRef.current = onIncomingNotification;

    const loadNotifications = useCallback(
        async (silent = false) => {
            if (!enabled) return;
            if (!silent) setIsLoading(true);

            const response = await logServiceListNotifications({ query: { showRead: true } });
            if (response.error) {
                if (!silent) showErrorToast();
            } else {
                setNotifications((response.data.notifications ?? []).filter(shouldShowNotificationInCenter));
            }

            if (!silent) setIsLoading(false);
        },
        [enabled]
    );

    useEffect(() => {
        if (!enabled) {
            setNotifications([]);
            setIsLoading(false);
            return;
        }

        void loadNotifications();
        return subscribeNotificationStream({
            onNotification: (notification) => {
                if (!shouldShowNotificationInCenter(notification)) return;

                setNotifications((current) => {
                    const withoutDuplicate = current.filter((item) => item.id !== notification.id);
                    return [notification, ...withoutDuplicate];
                });
                onIncomingNotificationRef.current?.(notification);
            },
            onReconnect: () => void loadNotifications(true),
        });
    }, [enabled, loadNotifications]);

    const toggleRead = useCallback(async (id: string) => {
        const response = await logServiceReadNotify({ path: { id } });
        if (response.error) {
            showErrorToast();
            return;
        }

        setNotifications((current) =>
            current.map((notification) =>
                notification.id === id
                    ? { ...notification, readAt: notification.readAt ? undefined : new Date().toISOString() }
                    : notification
            )
        );
    }, []);

    const deleteNotification = useCallback(async (id: string) => {
        const response = await logServiceDeleteNotify({ path: { id } });
        if (response.error) {
            showErrorToast();
            return;
        }

        setNotifications((current) => current.filter((notification) => notification.id !== id));
    }, []);

    const markAllAsRead = useCallback(async () => {
        const ids = notifications.flatMap((notification) =>
            !notification.readAt && notification.id ? [notification.id] : []
        );
        if (ids.length === 0) return;

        setIsMutating(true);
        const results = await Promise.all(
            ids.map(async (id) => ({ id, response: await logServiceReadNotify({ path: { id } }) }))
        );
        const successfulIds = new Set(results.filter(({ response }) => !response.error).map(({ id }) => id));

        setNotifications((current) =>
            current.map((notification) =>
                notification.id && successfulIds.has(notification.id)
                    ? { ...notification, readAt: new Date().toISOString() }
                    : notification
            )
        );
        setIsMutating(false);

        if (successfulIds.size !== ids.length) showErrorToast();
    }, [notifications]);

    const deleteAll = useCallback(async () => {
        const ids = notifications.flatMap((notification) => (notification.id ? [notification.id] : []));
        if (ids.length === 0) return;

        setIsMutating(true);
        const results = await Promise.all(
            ids.map(async (id) => ({ id, response: await logServiceDeleteNotify({ path: { id } }) }))
        );
        const successfulIds = new Set(results.filter(({ response }) => !response.error).map(({ id }) => id));

        setNotifications((current) =>
            current.filter((notification) => !notification.id || !successfulIds.has(notification.id))
        );
        setIsMutating(false);

        if (successfulIds.size !== ids.length) showErrorToast();
    }, [notifications]);

    return {
        notifications,
        unreadCount: notifications.filter((notification) => !notification.readAt).length,
        isLoading,
        isMutating,
        toggleRead,
        markAllAsRead,
        deleteNotification,
        deleteAll,
    };
};
